import { useEffect, useState } from 'react';
import { Copy, Eye, RefreshCw, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import type { FormDetail } from '@kaba/shared';
import { api } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { navigate } from '../../../lib/router';
import { Button, cx } from '../../ui/ui';
import type { Def } from './model';

const inp = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-9 sm:text-sm';

function Box({ title, children, hint }: { title: string; children: React.ReactNode; hint?: string }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 sm:p-4" aria-label={title}>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <p className="text-xs text-[var(--ink-soft)]">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const toLocal = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const pick = (d: FormDetail) => ({
  access: d.access,
  opensAt: d.opensAt,
  closesAt: d.closesAt,
  closedMessage: d.closedMessage,
  maxSubmissions: d.maxSubmissions,
  dailyLimit: d.dailyLimit,
  notify: d.notify,
  publicEnabled: d.publicEnabled,
});

/** Zugang & Abschluss (A01, D03, E02, V01) */
export function AccessTab({ def, detail, onChange, onReload, onViewVersion }: { def: Def; detail: FormDetail; onChange: (d: Def) => void; onReload: () => void; onViewVersion: (id: string) => void }) {
  const [s, setS] = useState(pick(detail));
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => setS(pick(detail)), [detail]);
  const dirty = JSON.stringify(s) !== JSON.stringify(pick(detail));
  const b = detail.board;
  const link = `${location.origin}${detail.fillPath}`;
  const hasFile = def.pages.some((p) => p.items.some((i) => (i.kind === 'question' && i.type === 'file') || (i.kind === 'section' && i.items.some((q) => q.type === 'file'))));
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));
  const people = b.people.filter((p) => !query || p.name.toLowerCase().includes(query.toLowerCase()));
  const action = async (a: string, confirm?: string) => {
    if (confirm && !window.confirm(confirm)) return;
    try {
      await api.post(`/api/forms/${detail.id}/${a}`);
      onReload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div className="flex flex-col gap-4">
        <Box title="Wer darf ausfüllen?" hint="Einsenden erlaubt weder das Board zu lesen noch Karten zu bearbeiten. Beim Absenden wird erneut geprüft.">
          {b.teamId ? (
            <>
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <input type="checkbox" className="size-5" checked={s.access.allTeam} onChange={(e) => setS({ ...s, access: { ...s.access, allTeam: e.target.checked } })} />
                Alle aktuellen Mitglieder dieses Teams
              </label>
              {!s.access.allTeam && b.groups.length > 0 && (
                <fieldset className="flex flex-col gap-1 text-sm">
                  <legend className="mb-1 font-medium">Gruppen</legend>
                  <div className="flex flex-wrap gap-1.5">
                    {b.groups.map((g) => (
                      <label key={g.id} className="flex min-h-10 items-center gap-1.5 rounded-full border border-[var(--line)] px-3 has-[:checked]:border-accent has-[:checked]:bg-accent-soft">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={s.access.groupIds.includes(g.id)}
                          onChange={(e) => setS({ ...s, access: { ...s.access, groupIds: e.target.checked ? [...s.access.groupIds, g.id] : s.access.groupIds.filter((x) => x !== g.id) } })}
                        />
                        {g.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </>
          ) : (
            <p className="text-xs text-[var(--ink-soft)]">Das Board gehört zu keinem Team – wähle einzelne Personen.</p>
          )}
          <fieldset className="flex flex-col gap-1 text-sm">
            <legend className="mb-1 font-medium">Einzelne Personen</legend>
            {b.people.length > 8 && <input className={inp} placeholder="Person suchen …" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Person suchen" />}
            <div className="flex max-h-48 flex-col overflow-y-auto">
              {people.map((p) => (
                <label key={p.id} className="flex min-h-10 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={s.access.userIds.includes(p.id)}
                    onChange={(e) => setS({ ...s, access: { ...s.access, userIds: e.target.checked ? [...s.access.userIds, p.id] : s.access.userIds.filter((x) => x !== p.id) } })}
                  />
                  {p.name}
                </label>
              ))}
            </div>
          </fieldset>
        </Box>

        <Box title="Öffentlicher Link" hint="Ohne Anmeldung: Wer den Link hat, kann einsenden – sonst nichts. Kein Einblick ins Board, keine Namen, keine Karten.">
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input type="checkbox" className="size-5" checked={s.publicEnabled} onChange={(e) => setS({ ...s, publicEnabled: e.target.checked })} />
            Öffentlich ausfüllbar
          </label>
          {s.publicEnabled && hasFile && !detail.scanner && (
            <p role="alert" className="rounded-lg bg-amber-50 p-2.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              Dieses Formular hat eine Dateifrage. Öffentlich geht das nur mit Virenprüfung – auf diesem Server ist keine eingerichtet (KABA_CLAMAV). Entferne die Dateifrage oder lass die Prüfung einrichten.
            </p>
          )}
          {detail.publicEnabled && detail.publicUrl && (
            <>
              <div className="flex gap-2">
                <input className={inp} readOnly value={detail.publicUrl} aria-label="Öffentlicher Formularlink" onFocus={(e) => e.target.select()} />
                <Button
                  onClick={async () => {
                    await navigator.clipboard?.writeText(detail.publicUrl!).catch(() => undefined);
                    toast.info('Link kopiert.');
                  }}
                >
                  <Copy size={15} /> Kopieren
                </Button>
              </div>
              <div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (!window.confirm('Link ersetzen? Der bisherige Link funktioniert sofort nicht mehr, begonnene Entwürfe darüber gehen verloren.')) return;
                    try {
                      await api.post(`/api/forms/${detail.id}/link`);
                      toast.info('Neuer Link erstellt.');
                      onReload();
                    } catch (e) {
                      toast.error((e as Error).message);
                    }
                  }}
                >
                  <RefreshCw size={14} /> Link ersetzen
                </Button>
              </div>
            </>
          )}
          {s.publicEnabled && (
            <label className="flex flex-col gap-1 text-sm font-medium">
              Höchstens Einsendungen pro Tag
              <input className={inp} inputMode="numeric" placeholder="Voreinstellung des Servers" value={s.dailyLimit ?? ''} onChange={(e) => setS({ ...s, dailyLimit: e.target.value ? Math.max(1, Number(e.target.value) || 1) : null })} />
            </label>
          )}
          {s.publicEnabled && !detail.publicUrl && <p className="text-xs text-[var(--ink-soft)]">Der Link entsteht beim Speichern.</p>}
        </Box>

        <Box title="Bei neuer Einsendung melden" hint="Über Eingang, Push oder E-Mail – je nach den Einstellungen der Person. Nur wer das Board sehen darf.">
          {b.teamId && b.groups.length > 0 && (
            <fieldset className="flex flex-col gap-1 text-sm">
              <legend className="mb-1 font-medium">Gruppen</legend>
              <div className="flex flex-wrap gap-1.5">
                {b.groups.map((g) => (
                  <label key={g.id} className="flex min-h-10 items-center gap-1.5 rounded-full border border-[var(--line)] px-3 has-[:checked]:border-accent has-[:checked]:bg-accent-soft">
                    <input type="checkbox" className="sr-only" checked={s.notify.groupIds.includes(g.id)} onChange={(e) => setS({ ...s, notify: { ...s.notify, groupIds: toggle(s.notify.groupIds, g.id, e.target.checked) } })} />
                    {g.name}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <fieldset className="flex flex-col gap-1 text-sm">
            <legend className="mb-1 font-medium">Personen</legend>
            <div className="flex max-h-48 flex-col overflow-y-auto">
              {b.people.map((p) => (
                <label key={p.id} className="flex min-h-10 items-center gap-2">
                  <input type="checkbox" checked={s.notify.userIds.includes(p.id)} onChange={(e) => setS({ ...s, notify: { ...s.notify, userIds: toggle(s.notify.userIds, p.id, e.target.checked) } })} />
                  {p.name}
                </label>
              ))}
            </div>
          </fieldset>
        </Box>

        <Box title="Zeitraum und Menge">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm font-medium">
              Geöffnet ab
              <input className={inp} type="datetime-local" value={toLocal(s.opensAt)} onChange={(e) => setS({ ...s, opensAt: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Geschlossen ab
              <input className={inp} type="datetime-local" value={toLocal(s.closesAt)} onChange={(e) => setS({ ...s, closesAt: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </label>
            <label className="flex flex-col gap-1 text-sm font-medium">
              Höchstens Einsendungen
              <input className={inp} inputMode="numeric" placeholder="unbegrenzt" value={s.maxSubmissions ?? ''} onChange={(e) => setS({ ...s, maxSubmissions: e.target.value ? Math.max(1, Number(e.target.value) || 1) : null })} />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-sm font-medium">
            Hinweis nach Ablauf
            <input className={inp} maxLength={500} placeholder="Die Anmeldung ist geschlossen." value={s.closedMessage ?? ''} onChange={(e) => setS({ ...s, closedMessage: e.target.value || null })} />
          </label>
        </Box>
        <div className="flex justify-end">
          <Button
            variant="primary"
            disabled={!dirty || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.put(`/api/forms/${detail.id}/settings`, s);
                toast.info('Zugang gespeichert.');
                onReload();
              } catch (e) {
                toast.error((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Zugang speichern
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <Box title="Abschluss" hint="Erscheint nach dem Absenden zusammen mit der Eingangsnummer.">
          <label className="flex flex-col gap-1 text-sm font-medium">
            Bestätigung
            <textarea className={cx(inp, 'min-h-16 py-2')} maxLength={1000} value={def.confirmation.text} onChange={(e) => onChange({ ...def, confirmation: { ...def.confirmation, text: e.target.value } })} />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            Bearbeitungserwartung (optional)
            <input className={inp} maxLength={300} placeholder="z. B. Wir melden uns innerhalb einer Woche." value={def.confirmation.expectation ?? ''} onChange={(e) => onChange({ ...def, confirmation: { ...def.confirmation, expectation: e.target.value || undefined } })} />
          </label>
        </Box>

        <Box title="Interner Formularlink" hint="Für angemeldete Personen mit Zugang. Der Link bleibt bei neuen Versionen gleich.">
          <div className="flex gap-2">
            <input className={inp} readOnly value={link} aria-label="Formularlink" onFocus={(e) => e.target.select()} />
            <Button
              onClick={async () => {
                await navigator.clipboard?.writeText(link).catch(() => undefined);
                toast.info('Link kopiert.');
              }}
            >
              <Copy size={15} /> Kopieren
            </Button>
          </div>
        </Box>

        <Box title="Testfälle" hint="Beispielantworten, die vor jeder Veröffentlichung geprüft werden. Neue Testfälle speicherst du in der Vorschau.">
          {def.tests.length === 0 && <p className="text-xs text-[var(--ink-soft)]">Noch keine Testfälle.</p>}
          <ul className="flex flex-col gap-1">
            {def.tests.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-sm">
                <span className="flex-1">{t.name}</span>
                <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:text-rose-600" aria-label={`Testfall „${t.name}“ löschen`} onClick={() => onChange({ ...def, tests: def.tests.filter((x) => x.id !== t.id) })}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </Box>

        <Box title="Versionen">
          {detail.versions.length === 0 && <p className="text-xs text-[var(--ink-soft)]">Noch nicht veröffentlicht.</p>}
          <ul className="flex flex-col divide-y divide-[var(--line)] text-sm">
            {detail.versions.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-2 py-1.5">
                <span className="flex-1">
                  Version {v.number} · {new Date(v.publishedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                  {v.publishedBy && ` · ${v.publishedBy}`}
                  {v.number === detail.activeVersion && <span className="ml-1 rounded bg-emerald-100 px-1 text-xs text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100">aktiv</span>}
                  {v.withdrawnAt && <span className="ml-1 rounded bg-rose-100 px-1 text-xs text-rose-900 dark:bg-rose-900 dark:text-rose-100">zurückgezogen</span>}
                </span>
                <Button size="sm" variant="ghost" onClick={() => onViewVersion(v.id)}>
                  <Eye size={14} /> Ansehen
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (!window.confirm(`Version ${v.number} als Entwurf übernehmen? Der aktuelle Entwurf wird ersetzt.`)) return;
                    await api.post(`/api/forms/${detail.id}/versions/${v.id}/restore`);
                    onReload();
                  }}
                >
                  <RotateCcw size={14} /> Als Entwurf
                </Button>
                {v.number !== detail.activeVersion && !v.withdrawnAt && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (!window.confirm(`Version ${v.number} zurückziehen? Begonnene Einsendungen dieser Version können dann nicht mehr abgeschickt werden; passende Antworten werden in die aktuelle Version übernommen.`)) return;
                      await api.post(`/api/forms/${detail.id}/versions/${v.id}/withdraw`);
                      onReload();
                    }}
                  >
                    <Undo2 size={14} /> Zurückziehen
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Box>

        <Box title="Formular">
          <div className="flex flex-wrap gap-2">
            {detail.status === 'active' && <Button onClick={() => void action('pause')}>Pausieren</Button>}
            {detail.status === 'paused' && <Button onClick={() => void action('resume')}>Wieder aufnehmen</Button>}
            <Button
              onClick={async () => {
                const r = await api.post<{ id: string }>(`/api/forms/${detail.id}/copy`);
                toast.info('Kopie als Entwurf angelegt.');
                navigate(`/b/${detail.boardId}/formulare/${r.id}`);
              }}
            >
              Kopieren
            </Button>
            {detail.status !== 'archived' && (
              <Button variant="ghost" className="text-rose-600" onClick={() => void action('archive', 'Formular archivieren? Der Link funktioniert dann nicht mehr. Entstandene Karten und Einsendungen bleiben erhalten.')}>
                Archivieren
              </Button>
            )}
          </div>
        </Box>
      </div>
    </div>
  );
}
